import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Mail, Send, Loader } from 'lucide-react';
import { submitContactForm } from '../services/api';

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    social: '',
    context: '',
    priority: 'casual-gm',
    pitch: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileReady, setTurnstileReady] = useState(false);
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);
  const modalRef = useRef(null);

  // Handle Escape key to close modal
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && !loading) {
      onClose();
    }
  }, [onClose, loading]);

  // Add/remove escape key listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  // Check if Turnstile script is loaded
  useEffect(() => {
    const checkTurnstile = () => {
      if (window.turnstile) {
        setTurnstileReady(true);
      }
    };

    // Check immediately
    checkTurnstile();

    // Also listen for script load
    window.addEventListener('load', checkTurnstile);
    
    // Poll for turnstile availability (backup)
    const interval = setInterval(() => {
      if (window.turnstile) {
        setTurnstileReady(true);
        clearInterval(interval);
      }
    }, 100);

    return () => {
      window.removeEventListener('load', checkTurnstile);
      clearInterval(interval);
    };
  }, []);

  // Render Turnstile widget when modal opens and script is ready
  useEffect(() => {
    if (isOpen && turnstileReady && turnstileRef.current && !widgetIdRef.current) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        try {
          widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
            sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY,
            callback: (token) => {
              setTurnstileToken(token);
            },
            'error-callback': () => {
              setError('Security verification failed. Please refresh and try again.');
            },
          });
        } catch (err) {
          console.error('Turnstile render error:', err);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isOpen, turnstileReady]);

  // Cleanup widget when modal closes
  useEffect(() => {
    if (!isOpen && widgetIdRef.current !== null) {
      try {
        window.turnstile.remove(widgetIdRef.current);
      } catch (err) {
        console.error('Turnstile remove error:', err);
      }
      widgetIdRef.current = null;
      setTurnstileToken('');
    }
  }, [isOpen]);

  const priorityOptions = [
    { value: 'casual-gm', label: 'Just a casual GM ☕' },
    { value: 'when-you-can', label: 'When you can get to it 📅' },
    { value: 'this-week', label: 'This week would be great 📌' },
    { value: 'urgent', label: 'Pretty urgent 🚨' },
    { value: 'fire', label: 'The building is on fire 🔥' }
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError('');

    try {
      const response = await submitContactForm({
        ...formData,
        turnstile_token: turnstileToken || 'not-required'
      });
      
      setSuccess(true);
      // Scroll modal to top so user sees success message
      if (modalRef.current) {
        modalRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
      // Show success message longer and provide clear feedback
      setTimeout(() => {
        setFormData({
          name: '',
          social: '',
          context: '',
          priority: 'casual-gm',
          pitch: '',
          email: ''
        });
        setSuccess(false);
        onClose();
      }, 4000);
    } catch (err) {
      console.error('Contact form error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to send message. Please try again.';
      setError(errorMessage);
      
      // Reset Turnstile on error so user can retry
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.reset(widgetIdRef.current);
          setTurnstileToken('');
        } catch (resetErr) {
          console.error('Turnstile reset error:', resetErr);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 animate-fadeIn"
      onClick={handleBackdropClick}
    >
      <div 
        ref={modalRef}
        className="bg-white w-full max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto relative rounded-2xl sm:rounded-3xl shadow-2xl animate-slideUp mx-1 sm:mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header - Executive slate color */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white p-4 sm:p-6 md:p-8 relative overflow-hidden rounded-t-2xl sm:rounded-t-3xl">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-2xl" />
            <div className="absolute bottom-10 right-10 w-40 h-40 bg-amber-400 rounded-full blur-3xl" />
          </div>

          <button
            onClick={onClose}
            type="button"
            className="absolute top-3 right-3 sm:top-6 sm:right-6 text-white/80 hover:text-white hover:bg-white/20 p-2 sm:p-3 rounded-full transition-all duration-300 hover:rotate-90 z-20"
            aria-label="Close modal"
          >
            <X size={20} className="sm:w-6 sm:h-6" />
          </button>
          
          <div className="relative z-10 pr-10 sm:pr-0">
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-4">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                <Mail size={16} className="sm:w-6 sm:h-6 text-amber-400" />
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">Let&apos;s Connect</h2>
            </div>
            <p className="text-sm sm:text-lg md:text-xl font-light opacity-95">
              Fill out the details below and I&apos;ll get back to you soon!
            </p>
          </div>
        </div>

        {/* Success Message */}
        {success && (
          <div className="p-6 sm:p-8 bg-green-50 border-b-4 border-green-500 text-center">
            <div className="text-4xl mb-2">✅</div>
            <p className="text-green-800 font-bold text-lg sm:text-xl mb-1">Message Sent Successfully!</p>
            <p className="text-green-700 text-sm sm:text-base">I'll get back to you soon. Check your email for confirmation.</p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="p-4 sm:p-6 bg-red-50 border-b-4 border-red-500">
            <p className="text-red-800 font-bold text-center text-sm sm:text-base">✗ {error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6">
          {/* Name */}
          <div>
            <label htmlFor="name" className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              placeholder="Your full name"
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg sm:rounded-xl focus:border-slate-500 focus:ring-2 focus:ring-slate-200 outline-none transition-all duration-300"
            />
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@example.com"
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg sm:rounded-xl focus:border-slate-500 focus:ring-2 focus:ring-slate-200 outline-none transition-all duration-300"
            />
          </div>

          {/* Social Handle */}
          <div>
            <label htmlFor="social" className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2">
              LinkedIn URL (or X/Social handle) <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="social"
              name="social"
              required
              value={formData.social}
              onChange={handleChange}
              placeholder="https://linkedin.com/in/yourprofile"
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg sm:rounded-xl focus:border-slate-500 focus:ring-2 focus:ring-slate-200 outline-none transition-all duration-300"
            />
          </div>

          {/* The Context */}
          <div>
            <label htmlFor="context" className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2">
              The Context <span className="text-red-500">*</span>
            </label>
            <p className="text-[10px] sm:text-xs text-gray-500 mb-1 sm:mb-2">What are we diving into today?</p>
            <textarea
              id="context"
              name="context"
              required
              value={formData.context}
              onChange={handleChange}
              placeholder="Tell me what's on your mind..."
              rows={3}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg sm:rounded-xl focus:border-slate-500 focus:ring-2 focus:ring-slate-200 outline-none transition-all duration-300 resize-none"
            />
          </div>

          {/* The Priority */}
          <div>
            <label htmlFor="priority" className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2">
              The Priority
            </label>
            <p className="text-[10px] sm:text-xs text-gray-500 mb-1 sm:mb-2">How urgent is this?</p>
            <select
              id="priority"
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg sm:rounded-xl focus:border-slate-500 focus:ring-2 focus:ring-slate-200 outline-none transition-all duration-300 cursor-pointer"
            >
              {priorityOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* The Pitch */}
          <div>
            <label htmlFor="pitch" className="block text-xs sm:text-sm font-bold text-gray-700 mb-1 sm:mb-2">
              The Pitch <span className="text-red-500">*</span>
            </label>
            <p className="text-[10px] sm:text-xs text-gray-500 mb-1 sm:mb-2">What&apos;s the one thing I should know?</p>
            <textarea
              id="pitch"
              name="pitch"
              required
              value={formData.pitch}
              onChange={handleChange}
              placeholder="Give me the elevator pitch..."
              rows={2}
              className="w-full px-3 sm:px-4 py-2 sm:py-3 text-sm sm:text-base border-2 border-gray-300 rounded-lg sm:rounded-xl focus:border-slate-500 focus:ring-2 focus:ring-slate-200 outline-none transition-all duration-300 resize-none"
            />
          </div>

          {/* Turnstile Widget */}
          <div ref={turnstileRef} className="flex justify-center min-h-[65px]">
            {!turnstileReady && (
              <div className="flex items-center gap-2 text-gray-500 text-xs sm:text-sm">
                <Loader size={14} className="animate-spin" />
                <span>Loading security check...</span>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-2 sm:pt-4">
            <button
              type="submit"
              disabled={loading || !turnstileToken}
              className="w-full py-3 sm:py-4 bg-slate-800 text-white font-bold text-sm sm:text-lg rounded-lg sm:rounded-xl hover:bg-slate-700 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 group shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader size={18} className="animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <Send size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
                  <span>Send Message</span>
                </>
              )}
            </button>
          </div>

          {/* Email info */}
          <div className="pt-2 sm:pt-4 border-t border-slate-200">
            <p className="text-xs sm:text-sm text-slate-600 text-center">
              Sending to: <span className="font-bold text-slate-800">hi@nishitsadhwani.com</span>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactModal;
